import { useState } from 'react'
import { useStore } from '../store/useStore'
import { 
  Menu, 
  X, 
  Play, 
  Square,
  LogIn,
  Info,
  Sparkles,
  FolderOpen,
  RefreshCw,
  Download,
  Globe,
  Image,
  LayoutTemplate,
  History,
  BookOpen
} from 'lucide-react'
import { downloadSketch } from '../utils/downloadSketch'
import { haptic } from '../utils/haptics'
import PublishModal from './PublishModal'
import TemplateModal from './TemplateModal'

export default function MobileHeader({ onAuthClick, onAboutClick, onChangelogClick, onBrowseClick, isTablet = false }) {
  const { 
    isAuthenticated,
    toggleAiPanel,
    p5Credentials,
    showConsole,
    toggleConsole,
    sketchName,
    files
  } = useStore()
  
  const [menuOpen, setMenuOpen] = useState(false)
  const [isRunning, setIsRunning] = useState(false)
  const [showPublish, setShowPublish] = useState(false)
  const [showTemplates, setShowTemplates] = useState(false)

  const handleRun = () => {
    haptic('medium')
    window.dispatchEvent(new CustomEvent('runSketch'))
    setIsRunning(true)
  }

  const handleStop = () => {
    haptic('light')
    window.dispatchEvent(new CustomEvent('stopSketch'))
    setIsRunning(false)
  }

  const handleSync = async () => {
    window.dispatchEvent(new CustomEvent('syncSketch'))
    setMenuOpen(false)
  }

  const handleDownload = () => {
    downloadSketch(sketchName, files)
    setMenuOpen(false)
  }

  // Icon sizes for tablet vs phone
  const iconSize = isTablet ? 28 : 24
  const buttonIconSize = isTablet ? 24 : 20

  return (
    <>
      <header 
        className={`bg-ide-surface border-b border-ide-border px-4 ${isTablet ? 'py-3' : 'py-2.5'}`}
        style={{ paddingTop: `max(${isTablet ? '0.75rem' : '0.625rem'}, env(safe-area-inset-top, 0px))` }}
      >
        <div className="flex items-center justify-between">
          {/* Left: Menu */}
          <button
            type="button"
            onClick={() => setMenuOpen(!menuOpen)}
            className={`text-ide-textMuted hover:bg-ide-surfaceHover hover:text-ide-text rounded-lg transition-all active:scale-95 ${isTablet ? 'p-2.5' : 'p-2'}`}
            aria-label="Menu"
          >
            {menuOpen ? <X size={iconSize} /> : <Menu size={iconSize} />}
          </button>

          {/* Center: Logo - H1 for SEO */}
          <div className="flex items-center gap-1.5">
            <h1 className={`text-ide-text font-semibold m-0 ${isTablet ? 'text-base' : 'text-sm'}`}>p5js AI Cloud IDE</h1>
          </div>

          {/* Right: Login + Run/Stop buttons */}
          <div className="flex items-center gap-2">
            {/* Login button - always visible when not authenticated */}
            {!isAuthenticated && (
              <button
                type="button"
                onClick={() => {
                  onAuthClick()
                }}
                className={`rounded-lg transition-all bg-ide-elevated text-ide-textMuted hover:bg-ide-surfaceHover hover:text-ide-text active:scale-95 ${isTablet ? 'p-2.5' : 'p-2'}`}
                aria-label="Login"
                title="Login / Sign Up"
              >
                <LogIn size={buttonIconSize} />
              </button>
            )}
            
            {/* Run/Stop button */}
            <button
              type="button"
              onClick={isRunning ? handleStop : handleRun}
              className={`rounded-lg transition-all active:scale-95 ${isTablet ? 'p-2.5' : 'p-2'} ${
                isRunning
                  ? 'bg-ide-error hover:bg-red-600'
                  : 'bg-ide-accent hover:bg-ide-accentHover'
              }`}
              aria-label={isRunning ? 'Stop' : 'Run'}
            >
              {isRunning ? <Square size={buttonIconSize} fill="white" /> : <Play size={buttonIconSize} fill="white" />}
            </button>
          </div>
        </div>
      </header>

      {/* Slide-out Menu */}
      {menuOpen && (
        <>
          {/* Overlay */}
          <div 
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 animate-fade-in"
            onClick={() => setMenuOpen(false)}
          />
          
          {/* Menu Panel - Wider on tablets */}
          <div className={`fixed top-0 left-0 bottom-0 bg-ide-surface z-50 shadow-modal overflow-y-auto animate-slide-up ${isTablet ? 'w-80' : 'w-72'}`}>
            <div className={`${isTablet ? 'p-5' : 'p-4'}`}>
              {/* Close button */}
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                className={`absolute top-4 right-4 text-ide-textMuted hover:bg-ide-surfaceHover hover:text-ide-text rounded-lg z-10 transition-all active:scale-95 ${isTablet ? 'p-2.5' : 'p-2'}`}
                aria-label="Close menu"
              >
                <X size={isTablet ? 22 : 18} />
              </button>

              {/* Menu Items - Larger touch targets on tablets */}
              <nav className={`mt-12 space-y-0.5 pb-20`}>
                
                {/* New from Template - Top of menu */}
                <button
                  type="button"
                  onClick={() => {
                    haptic('light')
                    setShowTemplates(true)
                    setMenuOpen(false)
                  }}
                  className={`w-full flex items-center gap-3 text-left bg-ide-accent/10 text-ide-accent border border-ide-accent/30 rounded-lg transition-all mb-3 ${
                    isTablet ? 'px-4 py-3.5 text-base' : 'px-3 py-3 text-sm'
                  }`}
                >
                  <LayoutTemplate size={isTablet ? 20 : 18} />
                  <span className="font-medium">New from Template</span>
                </button>
                
                <button
                  type="button"
                  onClick={() => {
                    toggleAiPanel()
                    setMenuOpen(false)
                  }}
                  className={`w-full flex items-center gap-3 text-left text-ide-text hover:bg-ide-surfaceHover active:bg-ide-elevated rounded-lg transition-all ${
                    isTablet ? 'px-4 py-3.5 text-base' : 'px-3 py-3 text-sm'
                  }`}
                >
                  <Sparkles size={isTablet ? 20 : 18} className="text-ide-accent" />
                  <span>AI Assistant</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    if (onBrowseClick) {
                      onBrowseClick()
                    }
                    setMenuOpen(false)
                  }}
                  className={`w-full flex items-center gap-3 text-left text-ide-text hover:bg-ide-surfaceHover active:bg-ide-elevated rounded-lg transition-all ${
                    isTablet ? 'px-4 py-3.5 text-base' : 'px-3 py-3 text-sm'
                  }`}
                >
                  <FolderOpen size={isTablet ? 20 : 18} className="text-ide-textMuted" />
                  <span>Browse Sketches</span>
                </button>

                <button
                  type="button"
                  onClick={handleSync}
                  disabled={!isAuthenticated}
                  className={`w-full flex items-center gap-3 text-left rounded-lg transition-all ${
                    isTablet ? 'px-4 py-3.5 text-base' : 'px-3 py-3 text-sm'
                  } ${
                    isAuthenticated ? 'text-ide-text hover:bg-ide-surfaceHover active:bg-ide-elevated' : 'text-ide-textDim cursor-not-allowed'
                  }`}
                >
                  <RefreshCw size={isTablet ? 20 : 18} className="text-ide-textMuted" />
                  <span>Sync to P5 Editor</span>
                  {!isAuthenticated && <span className="ml-auto text-xs text-ide-textDim">Login required</span>}
                </button>

                <button
                  type="button"
                  onClick={handleDownload}
                  className={`w-full flex items-center gap-3 text-left text-ide-text hover:bg-ide-surfaceHover active:bg-ide-elevated rounded-lg transition-all ${
                    isTablet ? 'px-4 py-3.5 text-base' : 'px-3 py-3 text-sm'
                  }`}
                >
                  <Download size={isTablet ? 20 : 18} className="text-ide-textMuted" />
                  <span>Download Sketch</span>
                </button>

                {/* Prominent Gallery Actions */}
                <div className="bg-ide-elevated rounded-lg p-2 mb-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowPublish(true)
                      setMenuOpen(false)
                    }}
                    className={`w-full flex items-center gap-3 text-left bg-ide-success/10 text-ide-success border border-ide-success/30 rounded-lg transition-all active:scale-[0.98] ${
                      isTablet ? 'px-4 py-3.5 text-base' : 'px-3 py-3 text-sm'
                    }`}
                  >
                    <Globe size={isTablet ? 20 : 18} />
                    <span className="font-medium">Publish to Gallery</span>
                  </button>

                  <a
                    href="/gallery/"
                    onClick={() => setMenuOpen(false)}
                    className={`w-full flex items-center gap-3 text-left bg-ide-surface border border-ide-border text-ide-text rounded-lg transition-all active:scale-[0.98] mt-2 ${
                      isTablet ? 'px-4 py-3.5 text-base' : 'px-3 py-3 text-sm'
                    }`}
                  >
                    <Image size={isTablet ? 20 : 18} className="text-ide-textMuted" />
                    <span>Browse Gallery</span>
                  </a>
                </div>

                <div className="border-t border-ide-border my-3"></div>

                {!isAuthenticated && (
                  <button
                    type="button"
                    onClick={() => {
                      onAuthClick()
                      setMenuOpen(false)
                    }}
                    className={`w-full flex items-center gap-3 text-left text-ide-text hover:bg-ide-surfaceHover active:bg-ide-elevated rounded-lg transition-all ${
                      isTablet ? 'px-4 py-3.5 text-base' : 'px-3 py-3 text-sm'
                    }`}
                  >
                    <LogIn size={isTablet ? 20 : 18} className="text-ide-textMuted" />
                    <span>Login / Sign Up</span>
                  </button>
                )}

                <a
                  href="/changelog"
                  onClick={(e) => {
                    e.preventDefault()
                    onChangelogClick()
                    setMenuOpen(false)
                  }}
                  className={`w-full flex items-center gap-3 text-left text-ide-text hover:bg-ide-surfaceHover active:bg-ide-elevated rounded-lg transition-all ${
                    isTablet ? 'px-4 py-3.5 text-base' : 'px-3 py-3 text-sm'
                  }`}
                >
                  <History size={isTablet ? 20 : 18} className="text-ide-textMuted" />
                  <span>Changelog</span>
                </a>

                <a
                  href="/research"
                  className={`w-full flex items-center gap-3 text-left text-ide-text hover:bg-ide-surfaceHover active:bg-ide-elevated rounded-lg transition-all ${
                    isTablet ? 'px-4 py-3.5 text-base' : 'px-3 py-3 text-sm'
                  }`}
                >
                  <BookOpen size={isTablet ? 20 : 18} className="text-ide-textMuted" />
                  <span>Research</span>
                </a>

                <a
                  href="/about"
                  onClick={(e) => {
                    e.preventDefault()
                    onAboutClick()
                    setMenuOpen(false)
                  }}
                  className={`w-full flex items-center gap-3 text-left text-ide-text hover:bg-ide-surfaceHover active:bg-ide-elevated rounded-lg transition-all ${
                    isTablet ? 'px-4 py-3.5 text-base' : 'px-3 py-3 text-sm'
                  }`}
                >
                  <Info size={isTablet ? 20 : 18} className="text-ide-textMuted" />
                  <span>About</span>
                </a>
              </nav>
            </div>
          </div>
        </>
      )}
      
      {/* Publish Modal */}
      {showPublish && <PublishModal onClose={() => setShowPublish(false)} />}
      
      {/* Template Modal */}
      {showTemplates && <TemplateModal onClose={() => setShowTemplates(false)} />}
    </>
  )
}
